import { useEffect, useRef, useState, useCallback } from "react";
import { useMotionValue, useSpring, useReducedMotion } from "framer-motion";

/* ═══════════════════════════════════════════
   GLOBAL EASING — 21st.dev expo curve
   ═══════════════════════════════════════════ */
export const EXPO_OUT = [0.16, 1, 0.3, 1];
export const EXPO_IN_OUT = [0.87, 0, 0.13, 1];

/* ═══════════════════════════════════════════
   SPRING CONFIGS
   ═══════════════════════════════════════════ */
export const springSmooth = { type: "spring", stiffness: 100, damping: 20 };
export const springBouncy = { type: "spring", stiffness: 400, damping: 25, mass: 0.8 };
export const springGentle = { type: "spring", stiffness: 60, damping: 15, mass: 1.2 };
export const springSnappy = { type: "spring", stiffness: 300, damping: 30 };

/* ═══════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════ */

// Animated count-up on viewport entry
export function useCountUp(target, duration = 1500, startOnView = true) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!startOnView || reduced) { setCount(target); return; }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (now) => {
            const p = Math.min((now - t0) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 4);
            setCount(Math.floor(eased * target));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration, startOnView, reduced]);

  return { count, ref };
}

// Magnetic button — cursor tracking with spring physics
export function useMagnetic(strength = 0.35) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });
  const ref = useRef(null);

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * strength);
    y.set((e.clientY - cy) * strength);
  }, [x, y, strength]);

  const onLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return { ref, springX, springY, onMove, onLeave };
}

// 3D tilt — mouse position tracking
export function useTilt(intensity = 15) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0 });

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ rotateY: px * intensity, rotateX: -py * intensity });
  }, [intensity]);

  const onLeave = useCallback(() => setTilt({ rotateX: 0, rotateY: 0 }), []);

  return { ref, tilt, onMove, onLeave };
}

// Text scramble hook — randomized character reveal
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZابتثجحخدذرزسشصضطظعغفقكلمنهوي٠١٢٣٤٥٦٧٨٩";
export function useTextScramble(text, delay = 0, speed = 30) {
  const [display, setDisplay] = useState("");
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) { setDisplay(text); return; }
    let iteration = 0;
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setDisplay(
          text.split("").map((ch, i) => {
            if (ch === " ") return " ";
            if (i < iteration) return text[i];
            return CHARS[Math.floor(Math.random() * CHARS.length)];
          }).join("")
        );
        iteration += 1 / 2;
        if (iteration >= text.length) {
          clearInterval(interval);
          setDisplay(text);
        }
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [text, delay, speed, reduced]);

  return display;
}

/* ═══════════════════════════════════════════
   ENTRANCE VARIANTS — expo easing
   ═══════════════════════════════════════════ */
export const fadeInUp = {
  hidden: { opacity: 0, y: 50, filter: "blur(8px)" },
  visible: (i = 0) => ({
    opacity: 1, y: 0, filter: "blur(0px)",
    transition: { duration: 0.8, delay: i * 0.08, ease: EXPO_OUT },
  }),
};

export const fadeInDown = {
  hidden: { opacity: 0, y: -40, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1, y: 0, filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.08, ease: EXPO_OUT },
  }),
};

export const fadeInScale = {
  hidden: { opacity: 0, scale: 0.85, filter: "blur(10px)" },
  visible: (i = 0) => ({
    opacity: 1, scale: 1, filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.08, ease: EXPO_OUT },
  }),
};

export const slideInLeft = {
  hidden: { opacity: 0, x: -80, filter: "blur(6px)" },
  visible: {
    opacity: 1, x: 0, filter: "blur(0px)",
    transition: { duration: 0.8, ease: EXPO_OUT },
  },
};

export const slideInRight = {
  hidden: { opacity: 0, x: 80, filter: "blur(6px)" },
  visible: {
    opacity: 1, x: 0, filter: "blur(0px)",
    transition: { duration: 0.8, ease: EXPO_OUT },
  },
};

export const clipRevealUp = {
  hidden: { clipPath: "inset(100% 0% 0% 0%)" },
  visible: (i = 0) => ({
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.9, delay: i * 0.08, ease: EXPO_OUT },
  }),
};

export const scaleRotateIn = {
  hidden: { opacity: 0, scale: 0.5, rotate: -15 },
  visible: (i = 0) => ({
    opacity: 1, scale: 1, rotate: 0,
    transition: { ...springBouncy, delay: i * 0.1 },
  }),
};

/* ═══════════════════════════════════════════
   STAGGER CONTAINERS
   ═══════════════════════════════════════════ */
export const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

export const staggerFast = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
};

/* ═══════════════════════════════════════════
   CINEMATIC PAGE ENTRANCE
   ═══════════════════════════════════════════ */
export const pageOrchestrator = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

export const pageChild = {
  hidden: { opacity: 0, y: 40, filter: "blur(8px)" },
  visible: {
    opacity: 1, y: 0, filter: "blur(0px)",
    transition: { duration: 0.8, ease: EXPO_OUT },
  },
};

/* ═══════════════════════════════════════════
   WORD-BY-WORD TEXT REVEAL
   ═══════════════════════════════════════════ */
export const wordRevealContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
};

export const wordReveal = {
  hidden: { opacity: 0, y: 30, rotateX: -60 },
  visible: {
    opacity: 1, y: 0, rotateX: 0,
    transition: { duration: 0.6, ease: EXPO_OUT },
  },
};

/* ═══════════════════════════════════════════
   HOVER / MICRO-INTERACTIONS
   ═══════════════════════════════════════════ */
export const hoverLift = {
  y: -10, scale: 1.03,
  transition: { ...springBouncy },
};

export const tapShrink = {
  scale: 0.96,
  transition: { type: "spring", stiffness: 500, damping: 30 },
};

/* ═══════════════════════════════════════════
   FLOATING / CONTINUOUS
   ═══════════════════════════════════════════ */
export const floatingY = {
  y: [0, -14, 0],
  transition: { duration: 5, repeat: Infinity, ease: "easeInOut" },
};

export const floatingRotate = {
  rotate: [0, 5, -5, 0],
  transition: { duration: 6, repeat: Infinity, ease: "easeInOut" },
};
